/**
 * Money / percent / margin formatting + the margin → approval/commission rubric.
 * Ported from the source pricing app's `shared/format.js`. The margin thresholds
 * are the contract with sales: below 65% the deal starts costing commission,
 * below 50% it needs explicit approval, below 40% no commission is paid and the
 * deal is not approved, and below 30% it is flat-out rejected (close it and you
 * owe Cinch). `marginVariant` maps to our DS Badge.
 */

import type { BadgeProps } from "@/components/ui/badge";

type Variant = NonNullable<BadgeProps["variant"]>;

export function formatPrice(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "$0.00";
  const num = Number(value);
  const decimalPart = num.toString().split(".")[1] ?? "";
  const decimals = Math.min(Math.max(2, decimalPart.length), 8);
  return `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: decimals })}`;
}

/** Money fixed to exactly 2 decimals — for headline aggregates where the
 *  sub-cent precision `formatPrice` keeps (for per-unit prices) is just noise. */
export function formatPrice2(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "$0.00";
  return `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${Number(value).toFixed(1)}%`;
}

/** Gross margin % from a price and its cost. 0 when price is 0 (avoids ÷0). */
export function calcGrossMargin(price: number, cost: number): number {
  if (!price) return 0;
  return ((price - cost) / price) * 100;
}

/** DS Badge variant for a margin band (source `marginColor`, error → danger). */
export function marginVariant(margin: number): Variant {
  if (margin >= 80) return "success";
  if (margin >= 65) return "info";
  if (margin >= 50) return "warning";
  if (margin >= 30) return "danger";
  // Below 30% is the one chip allowed to shout (solid = genuine attention-grabber).
  return "danger-solid";
}

export function marginLabel(margin: number): string {
  if (margin >= 80) return "Healthy";
  if (margin >= 65) return "Acceptable";
  if (margin >= 50) return "Low";
  if (margin >= 40) return "Needs Explicit Approval";
  if (margin >= 30) return "Not Approved";
  return "Absolutely Not";
}

/** Commission/approval note shown alongside the blended margin. Empty ≥65%. */
export function marginNote(margin: number): string {
  if (margin >= 65) return "";
  if (margin >= 50) return "Acceptable with commission reduction.";
  if (margin >= 40) return "Will most likely have a reduced commission — requires explicit approval.";
  if (margin >= 30) return "No commission will be paid on this deal, and it is not approved.";
  return "Absolutely not approved — you will pay Cinch if you close a deal at this rate.";
}

/** Alert headline for the below-65% warning banner. Matches the note bands. */
export function marginAlertTitle(margin: number): string {
  if (margin >= 50) return "Margin affects commission";
  if (margin >= 40) return "Margin requires explicit approval";
  if (margin >= 30) return "Not approved — no commission on this deal";
  return "Absolutely not approved";
}

/** True when the margin band carries a commission/approval consequence (<65%). */
export function marginNeedsAttention(margin: number): boolean {
  return margin < 65;
}

/** True in the bottom band (<30%) — the "you will pay Cinch" tier. */
export function marginAbsolutelyNot(margin: number): boolean {
  return margin < 30;
}

/** One row of the margin rubric — the thresholds + commentary sales lives by.
 *  Drives the "what do these statuses mean?" reference modal. */
export interface MarginBand {
  /** Human-readable margin range, e.g. "65% – 79.9%". */
  range: string;
  /** Status label — matches `marginLabel`. */
  label: string;
  /** DS Badge variant — matches `marginVariant`. */
  variant: Variant;
  /** What this band means for commission/approval. */
  description: string;
}

/** The full margin rubric, highest band first. Source of truth for the
 *  reference modal; the card itself uses the scalar helpers above. */
export const marginRubric: MarginBand[] = [
  {
    range: "80% and above",
    label: "Healthy",
    variant: "success",
    description: "Strong margin. No commission reduction or approval needed.",
  },
  {
    range: "65% – 79.9%",
    label: "Acceptable",
    variant: "info",
    description: "Healthy enough to close as-is. No commission reduction or approval needed.",
  },
  {
    range: "50% – 64.9%",
    label: "Low",
    variant: "warning",
    description: "Acceptable with commission reduction.",
  },
  {
    range: "40% – 49.9%",
    label: "Needs Explicit Approval",
    variant: "danger",
    description: "Will most likely have a reduced commission — requires explicit approval.",
  },
  {
    range: "30% – 39.9%",
    label: "Not Approved",
    variant: "danger",
    description: "No commission will be paid on this deal, and it is not approved.",
  },
  {
    range: "Below 30%",
    label: "Absolutely Not",
    variant: "danger-solid",
    description: "Absolutely not approved — you will pay Cinch if you close a deal at this rate.",
  },
];
